import { memo } from "react";
import { IconUploadExport } from "@resources/icons/Icons";
import { ANALYTICS_CHART_TYPE } from "@resources/Constants";
import { HVText, HVIcon } from "@repo/HVComponents";
import { useLang } from "@hooks/useLang";
import * as S from "./ChartCard.styled";
import { ChartCardProps } from "./Interfaces";
import { TimeLinePresenter } from "./AnalyticsChart/TimeLine";
import PeopleQueueChart from "./peopleQueueChart";
import CategoryChart from "./categoryChart";
import { ICON } from "../../../../constants/icons";
import StoppedVehicleStatus from "./stoppedVehicleChart/StoppedVehicleStatus";

function Presenter(props: ChartCardProps): JSX.Element {
  const {
    icon,
    title,
    setInsightChat,
    type,
    analyticsCSVDownload,
    isSingleLocation,
    analyticsFromReport,
    displayOptions,
    categoryType
  } = props;
  const { translate } = useLang();
  const checkCategoryChartAllowed = (onlyStatusAllowed = false) => {
    const commonTypes = [
      ANALYTICS_CHART_TYPE.CUMULATIVE_PEOPLE_COUNT,
      ANALYTICS_CHART_TYPE.PEOPLE_QUEUES,
      ANALYTICS_CHART_TYPE.PEOPLE_OCCUPANCY,
      ANALYTICS_CHART_TYPE.CUMULATIVE_SHOPPING_CART_COUNT,
      ANALYTICS_CHART_TYPE.SHOPPING_CARTS_IN_USE,
      ANALYTICS_CHART_TYPE.SHOPPING_CART_QUEUES,
      ANALYTICS_CHART_TYPE.CUMULATIVE_VEHICLE_COUNT,
      ANALYTICS_CHART_TYPE.VEHICLE_OCCUPANCY,
      ANALYTICS_CHART_TYPE.VEHICLE_QUEUES,
      ANALYTICS_CHART_TYPE.SLIP_AND_FALL,
      ANALYTICS_CHART_TYPE.CROWD_COUNTING,
      ANALYTICS_CHART_TYPE.STOPPED_VEHICLE_ANALYTICS,
      ANALYTICS_CHART_TYPE.SLOW_TRAFFIC
    ];

    if (onlyStatusAllowed) {
      return [
        ...commonTypes,
        ANALYTICS_CHART_TYPE.STOPPED_VEHICLE_ANALYTICS
      ].includes(type ?? "");
    }
    return commonTypes.includes(type ?? "");
  };

  const checkGenAIAllowed = () => {
    return [
      ANALYTICS_CHART_TYPE.CUMULATIVE_PEOPLE_COUNT,
      ANALYTICS_CHART_TYPE.PEOPLE_OCCUPANCY,
      ANALYTICS_CHART_TYPE.PEOPLE_QUEUES,
      ANALYTICS_CHART_TYPE.RETAIL_VISITOR_AREA_TRAFFIC,
      ANALYTICS_CHART_TYPE.SLIP_AND_FALL,
      ANALYTICS_CHART_TYPE.CROWD_COUNTING,
      ANALYTICS_CHART_TYPE.CUMULATIVE_SHOPPING_CART_COUNT,
      ANALYTICS_CHART_TYPE.SHOPPING_CARTS_IN_USE,
      ANALYTICS_CHART_TYPE.SHOPPING_CART_QUEUES,
      ANALYTICS_CHART_TYPE.CUMULATIVE_VEHICLE_COUNT,
      ANALYTICS_CHART_TYPE.VEHICLE_OCCUPANCY,
      ANALYTICS_CHART_TYPE.VEHICLE_QUEUES,
      ANALYTICS_CHART_TYPE.SLOW_TRAFFIC,
      ANALYTICS_CHART_TYPE.STOPPED_VEHICLE_ANALYTICS
    ].includes(type ?? "");
  };

  const checkDisplayOptions = (type: string): boolean => {
    if (!analyticsFromReport) return true;
    return !!displayOptions?.find((item) => item.includes(type));
  };

  return (
    <S.Container
      isCategoryAllowed={checkCategoryChartAllowed()}
      key={`${props.type}-${props.cardKey}`}
    >
      <S.TopMenu>
        <S.LeftSideMenu data-testid="analytics-title">
          <S.IconWrap>{icon}</S.IconWrap>
          {title}
        </S.LeftSideMenu>
        <S.RightSideMenu>
          {checkCategoryChartAllowed(true) && !props.analyticsFromReport && (
            <S.InsightContainer
              onClick={() => {
                analyticsCSVDownload?.(isSingleLocation || false); // 안전하게 optional chaining 사용
              }}
              data-testid="analytics-csv-download"
            >
              <S.InsightButton>
                <IconUploadExport width={20} height={20} />
              </S.InsightButton>
              {translate("csv_export")}
            </S.InsightContainer>
          )}
          <S.Divider />
          {checkGenAIAllowed() && !props.analyticsFromReport && (
            <S.InsightContainer
              onClick={() => {
                type && setInsightChat?.(type); // 안전하게 optional chaining 사용
              }}
              data-testid="analytics-insight"
            >
              <S.InsightButton>
                <HVIcon name="analytics" svgProps={{ ...ICON.SIZE_20 }} />
              </S.InsightButton>
              <HVText type="Body-sm" c="var(--hv-text-normal-secondary)">
                {translate("insight")}
              </HVText>
              <S.InsightBeta>Beta</S.InsightBeta>
            </S.InsightContainer>
          )}
        </S.RightSideMenu>
      </S.TopMenu>

      <TimeLinePresenter {...props} />
      {(type === ANALYTICS_CHART_TYPE.PEOPLE_QUEUES ||
        type === ANALYTICS_CHART_TYPE.SHOPPING_CART_QUEUES ||
        type === ANALYTICS_CHART_TYPE.VEHICLE_QUEUES) &&
        checkDisplayOptions("status") && <PeopleQueueChart {...props} />}
      {type === ANALYTICS_CHART_TYPE.STOPPED_VEHICLE_ANALYTICS &&
        checkDisplayOptions("stopped_vehicle_by_type") && (
          <StoppedVehicleStatus {...props} />
        )}
      {checkCategoryChartAllowed() && checkDisplayOptions("category") && (
        <CategoryChart
          widgetType={props.widgetType ?? ""}
          updatedCategoryAnalytics={props.updatedCategoryAnalytics}
          categoryData={props.categoryData ?? []}
          title={title ?? ""}
          isCategoryDataLoading={props.isCategoryDataLoading}
          isReport={props.analyticsFromReport}
          categoryType={categoryType}
        />
      )}
    </S.Container>
  );
}

export const ChartCardPresenter = memo(Presenter);
